import { createSelector } from '@ngrx/store';

import {
  BillOfMaterialsState,
  SchaefflerBillOfMaterialsState,
} from '@cdba/core/store/reducers/detail/detail.reducer';
import {
  RawMaterialAnalysis,
  SchaefflerBomIdentifier,
  SchaefflerBomItem,
} from '@cdba/shared/models';
import {
  aggregateRawMaterials,
  calculateCostAndPrice,
  calculateRawMaterialsSummary,
  extractRawMaterials,
} from '@cdba/shared/utils';

import {
  isSchaefflerBillOfMaterialGuard,
  selectBillOfMaterialsSelectedItemRowId,
  selectBillOfMaterialsState,
} from './common-bom.selector';

export const selectSchaefflerTypedState = createSelector(
  selectBillOfMaterialsState,
  (state: BillOfMaterialsState): SchaefflerBillOfMaterialsState | undefined =>
    isSchaefflerBillOfMaterialGuard(state) ? state : undefined
);

export const selectSchaefflerItems = createSelector(
  selectSchaefflerTypedState,
  (state: SchaefflerBillOfMaterialsState): SchaefflerBomItem[] => state?.items
);

export const selectSelectedItem = createSelector(
  selectBillOfMaterialsSelectedItemRowId,
  selectSchaefflerItems,
  (rowId: string, items: SchaefflerBomItem[]): SchaefflerBomItem =>
    items?.find((item) => item.rowId === rowId)
);

export const selectMaterialDesignationOfSelectedItem = createSelector(
  selectSelectedItem,
  (item: SchaefflerBomItem): string => item?.materialDesignation
);

export const selectIdentifierOfSelectedItem = createSelector(
  selectSelectedItem,
  (item: SchaefflerBomItem): SchaefflerBomIdentifier => item?.bomIdentifier
);

export const selectCostAreaTotalValueOfSelectedItem = createSelector(
  selectSelectedItem,
  (item: SchaefflerBomItem): number | undefined =>
    item?.costing?.costAreaTotalValue
);

export const selectDirectChildrenOfSelectedItem = createSelector(
  selectBillOfMaterialsSelectedItemRowId,
  selectSchaefflerItems,
  (rowId: string, items: SchaefflerBomItem[]): SchaefflerBomItem[] => {
    const selectedItem = items?.find((item) => item.rowId === rowId);

    if (!selectedItem) {
      return [];
    }

    return items.filter(
      (item: SchaefflerBomItem) =>
        item.path.at(-2) === rowId && item.level === selectedItem.level + 1
    );
  }
);

export const selectAllChildrenOfSelectedItem = createSelector(
  selectBillOfMaterialsSelectedItemRowId,
  selectSchaefflerItems,
  (rowId: string, items: SchaefflerBomItem[]): SchaefflerBomItem[] => {
    const selectedItem = items?.find((item) => item.rowId === rowId);

    if (!selectedItem) {
      return [];
    }

    const children: SchaefflerBomItem[] = [];

    for (let i = items.indexOf(selectedItem) + 1; i < items.length; i += 1) {
      const item = items[i];
      if (item.level <= selectedItem.level) {
        break;
      } else {
        children.push(items[i]);
      }
    }

    return children;
  }
);

export const selectRawMaterialsAnalysisOfSelectedItem = createSelector(
  selectCostAreaTotalValueOfSelectedItem,
  selectAllChildrenOfSelectedItem,
  (
    costAreaTotalValue: number,
    children: SchaefflerBomItem[]
  ): RawMaterialAnalysis[] => {
    if (!costAreaTotalValue || !children) {
      return [];
    }

    const rawMaterials = extractRawMaterials(children);
    if (!rawMaterials) {
      return [];
    }

    const aggregatedRawMaterials = aggregateRawMaterials(rawMaterials);

    return calculateCostAndPrice(aggregatedRawMaterials, costAreaTotalValue);
  }
);

export const selectRawMaterialsAnalysisSummaryOfSelectedItem = createSelector(
  selectRawMaterialsAnalysisOfSelectedItem,
  (data: RawMaterialAnalysis[]): RawMaterialAnalysis[] => {
    if (!data) {
      return [];
    }

    return calculateRawMaterialsSummary(data);
  }
);
